/* eslint-disable @next/next/no-img-element */
'use client';
import styled from '@emotion/styled';
import { Box, Chip, Typography } from '@mui/material';
import { PortableText } from '@portabletext/react';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import imageUrlBuilder from '@sanity/image-url';
import dayjs from 'dayjs';
import Link from 'next/link';
import { ComponentProps } from 'react';

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
    min-height: 100vh;
  }
`;

const Heading = styled(Typography)`
  font-family: var(--font-cooper-lt-bt), sans-serif;
  font-size: 40px;
  line-height: 112.3%;
  align-items: center;
  color: #ffffff;
  margin-bottom: 32px;
  min-width: 409px;
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 20px;
    margin-top: 10px;
    min-width: unset;
  }
`;

const SubHeading = styled(Typography)`
  font-size: 28px;
  line-height: 112.3%;
  align-items: center;
  color: #ffffff;
  margin-bottom: 25px;
  margin-top: 30px;
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 18px;
    margin-top: 10px;
    min-width: unset;
  }
`;

const UpdateDate = styled(SubHeading)`
  font-size: 18px;
  margin-top: 10px;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 15px;
  & a {
    color: #4497aa;
    text-decoration: none;
  }
`;

const List = styled.ul`
  list-style-type: none;
`;
List.defaultProps = {
  className: 'career-list',
};

const ListItem = styled.li`
  position: relative;
  color: white;
  font-size: 16px;
  &::before {
    content: '\\2022';
    color: white;
    position: absolute;
    left: -25px;
    top: -8px;
    font-size: 30px;
  }
  p {
    margin-bottom: 15px;
  }
`;

const builder = imageUrlBuilder(sanityClient);

interface UpdateProps {
  update: {
    _id: string;
    _type: 'jobListing';
    _rev: string;
    _updatedAt: string;
    _createdAt: string;
    title: string;
    body: ComponentProps<typeof PortableText>['value'];
    tags?: {
      _createdAt: string;
      _rev: string;
      _type: string;
      _id: string;
      tag: string;
      _updatedAt: string;
    }[];
  }[];
}

function urlFor(source: any) {
  return builder.image(source);
}

const Update = (props: UpdateProps) => {
  return (
    <MainDiv>
      <Box key={props.update[0]._id} mb={8} id={`update-${props.update[0]._id}`} width={'100%'}>
        <Heading variant="h1">{props.update[0].title}</Heading>
        <UpdateDate variant="h2">
          {dayjs(props.update[0]._createdAt).format('MMMM D, YYYY')}
        </UpdateDate>
        {props.update[0].tags && (
          <Box my={4}>
            {props.update[0].tags.map(tag => (
              <Link key={tag._id} href={`/updates/tag/${tag._id}`}>
                <Chip label={tag.tag} style={{ marginRight: 10, backgroundColor: '#F6DBEE' }} />
              </Link>
            ))}
          </Box>
        )}
        <PortableText
          value={props.update[0].body}
          components={{
            block: {
              h1: ({ children }) => <Heading variant="h1">{children}</Heading>,
              h2: ({ children }) => <SubHeading variant="h2">{children}</SubHeading>,
              h3: ({ children }) => <SubHeading variant="h3">{children}</SubHeading>,
              h4: ({ children }) => <SubHeading variant="h4">{children}</SubHeading>,
              h5: ({ children }) => <SubHeading variant="h5">{children}</SubHeading>,
              h6: ({ children }) => <SubHeading variant="h6">{children}</SubHeading>,
              normal: ({ children }) => <Text>{children}</Text>,
            },
            list: ({ children }) => <List>{children}</List>,
            listItem: ({ children }) => <ListItem>{children}</ListItem>,
            types: {
              image: ({ value }) => {
                const url = urlFor(value).url();
                return (
                  <Box my={4}>
                    <img src={url} alt={url || 'Image'} style={{ maxWidth: '100%' }} />
                  </Box>
                );
              },
            },
          }}
        />
      </Box>
    </MainDiv>
  );
};

export default Update;
